import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, DatePicker, Button, Space, Typography, 
  Table, Tag, Select, Row, Col, 
  message, Descriptions, Modal, Radio, Statistic
} from 'antd';
import { 
  SearchOutlined, 
  // DownloadOutlined,  // 導出功能已移除
  EyeOutlined,
  UserOutlined,
  TeamOutlined  // 恢復引入，用於區分直屬/下級會員
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import reportService from '../../services/report.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// ===== 資料格式化工具函數（根據真實API數據修正）=====
const formatUtils = {
  // 投注類型中文化（根據實際API）
  getBetTypeName: (betType) => {
    const betTypeMap = {
      'position': { name: '名次', desc: '預測指定車號的名次位置' },
      'sum_value': { name: '冠亞和', desc: '預測車號總和大小' },
      'sum_big_small': { name: '冠亞和大小', desc: '預測車號總和大小（1-5小，6-10大）' },
      'sum_odd_even': { name: '冠亞和單雙', desc: '預測車號總和單雙性質' },
      'big_small': { name: '大小', desc: '預測車號大小' },
      'odd_even': { name: '單雙', desc: '預測車號單雙性質' },
      'dragon_tiger': { name: '龍虎', desc: '預測龍虎對戰結果' },
    };
    const result = betTypeMap[betType];
    return result ? result.name : betType;
  },

  // 獲取投注類型說明
  getBetTypeDescription: (betType) => {
    const betTypeMap = {
      'position': '預測指定車號的名次位置',
      'sum_value': '預測前兩名車號總和數值',
      'sum_big_small': '預測前兩名車號總和大小（小：3-10，大：11-18）',
      'sum_odd_even': '預測前兩名車號總和單雙性質',
      'big_small': '預測指定位置車號大小（小：1-5，大：6-10）',
      'odd_even': '預測指定位置車號單雙性質',
      'dragon_tiger': '預測指定位置龍虎對戰結果',
    };
    return betTypeMap[betType] || '其他投注類型';
  },

  // 投注內容友善化顯示（修復版本 - 包含完整位置和內容資訊）
  getBetContentDisplay: (betType, betContent, record = null) => {
    if (!betContent) return { main: '-', detail: '-', simple: '-' };
    
    try {
      // 處理陣列格式的betContent
      const content = Array.isArray(betContent) ? betContent : 
                     (typeof betContent === 'string' ? JSON.parse(betContent) : betContent);
      
      // 從 record 中獲取位置資訊
      const position = record?.position;
      
      switch(betType) {
        case 'position':
          // 名次投注：需要位置和車號資訊
          if (Array.isArray(content) && content.length > 0) {
            const carNumber = content[0];
            const positionText = position ? `第${position}名` : '名次';
            return {
              main: `${positionText} • 車號${carNumber}`,
              detail: `預測${positionText}為${carNumber}號車`,
              simple: `${positionText}-${carNumber}號`
            };
          }
          break;
          
        case 'big_small':
          // 大小投注：需要位置和大小資訊
          if (Array.isArray(content) && content.length > 0) {
            const type = content[0];
            const typeText = type === 'big' ? '大' : '小';
            const range = type === 'big' ? '(6-10)' : '(1-5)';
            const positionText = position ? `第${position}名` : '大小';
            return {
              main: `${positionText} • 下${typeText}`,
              detail: `預測${positionText}車號為${typeText}${range}`,
              simple: `${positionText}-${typeText}`
            };
          }
          break;
          
        case 'odd_even':
          // 單雙投注：需要位置和單雙資訊
          if (Array.isArray(content) && content.length > 0) {
            const type = content[0];
            const typeText = type === 'odd' ? '單' : '雙';
            const positionText = position ? `第${position}名` : '單雙';
            return {
              main: `${positionText} • 下${typeText}`,
              detail: `預測${positionText}車號為${typeText}數`,
              simple: `${positionText}-${typeText}`
            };
          }
          break;
          
        case 'sum_value':
          // 冠亞和值投注：具體數值
          if (Array.isArray(content) && content.length > 0) {
            const sumValue = content[0];
            return {
              main: `冠亞和值 • ${sumValue}`,
              detail: `預測前兩名車號總和為${sumValue}`,
              simple: `和值${sumValue}`
            };
          }
          break;
          
        case 'sum_big_small':
          // 冠亞和大小投注
          if (Array.isArray(content) && content.length > 0) {
            const type = content[0];
            const typeText = type === 'big' ? '大' : '小';
            const range = type === 'big' ? '(11-18)' : '(3-10)';
            return {
              main: `冠亞和 • 下${typeText}`,
              detail: `預測前兩名車號總和為${typeText}${range}`,
              simple: `和${typeText}`
            };
          }
          break;
          
        case 'sum_odd_even':
          // 冠亞和單雙投注
          if (Array.isArray(content) && content.length > 0) {
            const type = content[0];
            const typeText = type === 'odd' ? '單' : '雙';
            return {
              main: `冠亞和 • 下${typeText}`,
              detail: `預測前兩名車號總和為${typeText}數`,
              simple: `和${typeText}`
            };
          }
          break;
          
        case 'dragon_tiger':
          // 龍虎投注：需要指明哪個位置的龍虎
          if (Array.isArray(content) && content.length > 0) {
            const type = content[0];
            const typeText = type === 'dragon' ? '龍' : '虎';
            const positionText = position ? `第${position}名` : '龍虎';
            return {
              main: `${positionText} • 下${typeText}`,
              detail: `預測${positionText}龍虎對戰結果為${typeText}`,
              simple: `${positionText}-${typeText}`
            };
          }
          break;
          
        // 保留原有的格式處理（向下兼容）
        case 'champion':
          if (content.position && content.carNumber) {
            return {
              main: `第${content.position}名 • 車號${content.carNumber}`,
              detail: `預測第${content.position}名冠軍為${content.carNumber}號車`,
              simple: `${content.position}名-${content.carNumber}號`
            };
          }
          break;
          
        case 'place':
          if (content.position && content.carNumber) {
            return {
              main: `第${content.position}名 • 車號${content.carNumber}`,
              detail: `預測第${content.position}名為${content.carNumber}號車`,
              simple: `${content.position}名-${content.carNumber}號`
            };
          }
          break;
          
        default:
          // 未知格式的智能處理
          if (Array.isArray(content)) {
            const positionText = position ? `第${position}名 • ` : '';
            return {
              main: `${positionText}${content.join('、')}`,
              detail: `投注內容：${positionText}${content.join('、')}`,
              simple: `${positionText}${content.join(',')}`
            };
          } else if (typeof content === 'object') {
            return {
              main: '請查看詳情',
              detail: JSON.stringify(content),
              simple: '詳情'
            };
          } else {
            const positionText = position ? `第${position}名 • ` : '';
            return {
              main: `${positionText}${String(content)}`,
              detail: `投注內容：${positionText}${content}`,
              simple: `${positionText}${String(content)}`
            };
          }
      }
      
      // 如果沒有匹配到任何格式，但有位置資訊
      const positionText = position ? `第${position}名 • ` : '';
      return {
        main: `${positionText}請查看詳情`,
        detail: `${positionText}原始數據：${JSON.stringify(content)}`,
        simple: `${positionText}詳情`
      };
      
    } catch (e) {
      return {
        main: '格式錯誤',
        detail: `投注內容格式錯誤：${e.message}`,
        simple: '錯誤'
      };
    }
  },

  // 投注狀態中文化（根據真實API修正）
  getStatusDisplay: (status) => {
    const statusMap = {
      'pending': { 
        text: '待開獎', 
        color: 'processing', 
        desc: '等待開獎結果，投注金額已扣除',
        icon: '🕒',
        bgColor: '#e6f7ff'
      },
      'win': { 
        text: '中獎', 
        color: 'success', 
        desc: '投注成功中獎，獎金已發放',
        icon: '🎉',
        bgColor: '#f6ffed'
      },
      'lose': { 
        text: '未中獎', 
        color: 'error', 
        desc: '投注未中獎，投注金額不退還',
        icon: '❌',
        bgColor: '#fff2f0'
      },
      'cancelled': { 
        text: '已取消', 
        color: 'default', 
        desc: '投注已取消，投注金額已退還',
        icon: '➖',
        bgColor: '#f5f5f5'
      },
      // 保留原有的狀態（以防萬一）
      'won': { 
        text: '中獎', 
        color: 'success', 
        desc: '投注成功中獎，獎金已發放',
        icon: '🎉',
        bgColor: '#f6ffed'
      },
      'lost': { 
        text: '未中獎', 
        color: 'error', 
        desc: '投注未中獎，投注金額不退還',
        icon: '❌',
        bgColor: '#fff2f0'
      }
    };
    return statusMap[status] || { 
      text: status, 
      color: 'default', 
      desc: status,
      icon: '❓',
      bgColor: '#fafafa'
    };
  },

  // 會員類型顯示（以投注當下代理為主，缺失時回退目前所屬）
  getMemberTypeDisplay: (record, currentUser) => {
    const currentAgentId = currentUser?.id;
    const betAgentId = record?.agentId;            // 投注當下的代理ID（最準）
    const userCurrentAgentId = record?.user?.agentId; // 會員目前所屬代理ID（回退）

    if (betAgentId && currentAgentId && betAgentId === currentAgentId) {
      return { text: '直屬會員', color: 'blue', icon: <UserOutlined /> };
    }
    if (!betAgentId && userCurrentAgentId && currentAgentId && userCurrentAgentId === currentAgentId) {
      return { text: '直屬會員', color: 'blue', icon: <UserOutlined /> };
    }
    if (betAgentId || userCurrentAgentId) {
      return { text: '下級會員', color: 'green', icon: <TeamOutlined /> };
    }
    return { text: '未歸屬', color: 'default', icon: <TeamOutlined /> };
  },

  // 盈虧金額格式化
  formatWinLoss: (betAmount, winAmount) => {
    const winLoss = winAmount - betAmount;  // 會員盈虧
    const agentProfit = -winLoss;  // 代理盈虧
    
    return {
      memberWinLoss: winLoss,
      agentProfit: agentProfit,
      isProfit: agentProfit > 0,
      displayText: agentProfit > 0 ? `+${agentProfit.toLocaleString()}` : agentProfit.toLocaleString(),
      memberText: winLoss > 0 ? `+${winLoss.toLocaleString()}` : winLoss.toLocaleString()
    };
  },

  // 時間格式化（台灣習慣）
  formatDateTime: (dateTime) => {
    return dayjs(dateTime).format('YYYY/MM/DD HH:mm:ss');
  },

  // 代理路徑簡化顯示
  formatAgentPath: (agentPath) => {
    if (!agentPath || agentPath.length === 0) return '-';
    if (agentPath.length === 1) return agentPath[0].name;
    return `${agentPath[0].name}...${agentPath[agentPath.length - 1].name}`;
  },

  // 開獎結果格式化
  formatGameResult: (result) => {
    if (!result || !Array.isArray(result)) return '-';
    return result.map((car, index) => 
      `第${index + 1}名:${car}號`
    ).join(' | ');
  }
};

const BettingRecords = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ]);
  const [filters, setFilters] = useState({
    userId: '',
    betType: '',
    status: '',
    memberType: 'all'
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [quickSelect, setQuickSelect] = useState('today');
  const [summary, setSummary] = useState({
    totalBets: 0,
    totalAmount: 0,
    totalWinAmount: 0,
    totalValidAmount: 0
  });
  
  const [memberList, setMemberList] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const loadMemberList = useCallback(async () => {
    try {
      setLoadingMembers(true);
      const response = await reportService.getAllMembers();
      
      // 修正：處理新的響應格式
      if (response.success && response.data) {
        setMemberList(response.data);
      } else if (response.members) {
        // 向下相容處理
        setMemberList(response.members);
      } else {
        console.warn('無法識別會員列表格式:', response);
        setMemberList([]);
      }
    } catch (error) {
      console.error('載入會員列表失敗:', error);
      setMemberList([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  const loadBettingRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };
      
      const response = await reportService.getBettingRecords(params);
      
      if (response.success) {
        setRecords(response.records);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total
        }));
        setSummary(response.summary);
      }
    } catch (error) {
      console.error('載入投注記錄失敗:', error);
      message.error('載入投注記錄失敗');
    } finally {
      setLoading(false);
    }
  }, [dateRange, filters, pagination.current, pagination.pageSize]); // 修正依賴陣列

  useEffect(() => {
    loadBettingRecords();
    loadMemberList();
  }, [loadBettingRecords, loadMemberList]);

  const handleQuickSelect = (value) => {
    setQuickSelect(value);
    let start, end;
    
    switch(value) {
      case 'today':
        start = dayjs().startOf('day');
        end = dayjs().endOf('day');
        break;
      case 'yesterday':
        start = dayjs().subtract(1, 'day').startOf('day');
        end = dayjs().subtract(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        start = dayjs().startOf('week');
        end = dayjs().endOf('week');
        break;
      case 'lastWeek':
        start = dayjs().subtract(1, 'week').startOf('week');
        end = dayjs().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        start = dayjs().startOf('month');
        end = dayjs().endOf('month');
        break;
      case 'lastMonth':
        start = dayjs().subtract(1, 'month').startOf('month');
        end = dayjs().subtract(1, 'month').endOf('month');
        break;
      default:
        return;
    }
    
    setDateRange([start, end]);
  };

  const showDetail = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  // 導出功能已移除 - 待後續實現
  // const handleExport = async () => { ... };

  // ===== 優化的表格列配置 =====
  const columns = [
    {
      title: '投注時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date) => (
        <div style={{ fontSize: '12px' }}>
          <div>{dayjs(date).format('MM/DD')}</div>
          <div style={{ color: '#666' }}>{dayjs(date).format('HH:mm:ss')}</div>
        </div>
      )
    },
    {
      title: '回合',
      dataIndex: 'roundId',
      key: 'roundId',
      width: 80,
      render: (roundId) => (
        <Text code style={{ fontSize: '11px' }}>
          {roundId?.slice(-6) || '-'}
        </Text>
      )
    },
    {
      title: '會員資訊',
      key: 'userInfo',
      width: 160,
      render: (_, record) => {
        const memberType = formatUtils.getMemberTypeDisplay(record, user);
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              {memberType.icon}
              <Text strong style={{ fontSize: '13px' }}>
                {record.user?.displayName || record.userId}
              </Text>
            </Space>
            <Tag color={memberType.color} size="small">
              {memberType.text}
            </Tag>
          </Space>
        );
      }
    },
    {
      title: '代理資訊',
      key: 'agentInfo',
      width: 140,
      render: (_, record) => {
        // 修復：確保能正確顯示投注時的代理資訊
        const agentName = record.agent?.displayName || 
                         record.agent?.username || 
                         (record.agentId ? `代理ID: ${record.agentId.slice(-6)}` : null);
        
        return (
          <Space direction="vertical" size={2}>
            <Text strong style={{ fontSize: '12px' }}>
              {agentName || '-'}
            </Text>
          </Space>
        );
      }
    },
    {
      title: '投注內容',
      key: 'betInfo',
      width: 180,
      render: (_, record) => {
        const betTypeName = formatUtils.getBetTypeName(record.betType);
        const betContent = formatUtils.getBetContentDisplay(record.betType, record.betContent, record);
        
        return (
          <Space direction="vertical" size={2}>
            <Tag color="blue" style={{ fontSize: '11px' }}>
              {betTypeName}
            </Tag>
            <Text style={{ fontSize: '12px' }}>
              {betContent.main}
            </Text>
          </Space>
        );
      }
    },
    {
      title: '投注金額',
      dataIndex: 'betAmount',
      key: 'betAmount',
      width: 90,
      align: 'right',
      render: (amount) => (
        <Text strong style={{ color: '#1890ff' }}>
          ${amount?.toLocaleString() || 0}
        </Text>
      )
    },
    {
      title: '中獎金額',
      dataIndex: 'winAmount',
      key: 'winAmount',
      width: 90,
      align: 'right',
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${amount?.toLocaleString() || 0}
        </Text>
      )
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        const statusInfo = formatUtils.getStatusDisplay(status);
        return (
          <Tag color={statusInfo.color} style={{ fontSize: '11px' }}>
            {statusInfo.text}
          </Tag>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 60,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => showDetail(record)}
          style={{ padding: '0 4px' }}
        >
          詳情
        </Button>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Title level={3}>投注記錄查詢</Title>
        
        {/* 篩選條件 */}
        <Space direction="vertical" size={16} style={{ width: '100%', marginBottom: 24 }}>
          {/* 日期選擇 */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Radio.Group 
                value={quickSelect} 
                onChange={(e) => handleQuickSelect(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="today">今日</Radio.Button>
                <Radio.Button value="yesterday">昨日</Radio.Button>
                <Radio.Button value="thisWeek">本週</Radio.Button>
                <Radio.Button value="lastWeek">上週</Radio.Button>
                <Radio.Button value="thisMonth">本月</Radio.Button>
                <Radio.Button value="lastMonth">上月</Radio.Button>
              </Radio.Group>
            </Col>
            <Col xs={24} lg={8}>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                showTime={{ format: 'HH:mm' }}
                format="YYYY/MM/DD HH:mm"
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
          
          {/* 篩選條件 */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Select
                placeholder="選擇會員"
                allowClear
                showSearch
                style={{ width: '100%' }}
                value={filters.userId}
                onChange={(value) => setFilters({ ...filters, userId: value })}
                loading={loadingMembers}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="">全部會員</Option>
                {memberList.map(member => (
                  <Option key={member.userId} value={member.userId}>
                    {member.displayName} ({member.userId})
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              {/* 會員類型篩選已簡化，只保留全部會員選項 */}
              <Select
                placeholder="會員範圍"
                style={{ width: '100%' }}
                value={filters.memberType}
                onChange={(value) => setFilters({ ...filters, memberType: value })}
              >
                <Option value="all">全部會員</Option>
                <Option value="direct">直屬會員</Option>
                <Option value="subordinate">下級會員</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Select
                placeholder="投注類型"
                allowClear
                style={{ width: '100%' }}
                value={filters.betType}
                onChange={(value) => setFilters({ ...filters, betType: value })}
              >
                <Option value="">全部</Option>
                <Option value="position">名次</Option>
                <Option value="sum_value">冠亞和</Option>
                <Option value="sum_big_small">冠亞和大小</Option>
                <Option value="sum_odd_even">冠亞和單雙</Option>
                <Option value="big_small">大小</Option>
                <Option value="odd_even">單雙</Option>
                <Option value="dragon_tiger">龍虎</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Select
                placeholder="狀態"
                allowClear
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
              >
                <Option value="">全部</Option>
                <Option value="pending">待開獎</Option>
                <Option value="win">中獎</Option>
                <Option value="lose">未中獎</Option>
                <Option value="cancelled">已取消</Option>
              </Select>
            </Col>
          </Row>
          
          <Row>
            <Col span={24}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={loadBettingRecords}
                  style={{ background: '#ff6600', borderColor: '#ff6600' }}
                >
                  查詢
                </Button>
                {/* 導出功能已移除 - 待後續實現 */}
                {/* <Button icon={<DownloadOutlined />} onClick={handleExport}>導出報表</Button> */}
              </Space>
            </Col>
          </Row>
        </Space>
        
        {/* 簡化統計資訊 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8}>
            <Card size="small">
              <Statistic
                title="總投注筆數"
                value={summary.totalBets}
                suffix="筆"
                valueStyle={{ fontSize: '18px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card size="small">
              <Statistic
                title="總投注金額"
                value={summary.totalAmount}
                prefix="$"
                precision={0}
                valueStyle={{ fontSize: '18px', color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="會員總盈虧"
                value={summary.totalWinAmount - summary.totalAmount}
                prefix="$"
                precision={0}
                valueStyle={{ 
                  fontSize: '18px',
                  color: (summary.totalWinAmount - summary.totalAmount) > 0 ? '#52c41a' : '#ff4d4f'
                }}
              />
            </Card>
          </Col>
        </Row>
        
        {/* 優化的投注記錄表格 */}
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 項，共 ${total} 筆記錄`,
            onChange: (page, pageSize) => {
              setPagination({
                ...pagination,
                current: page,
                pageSize
              });
            }
          }}
          scroll={{ x: 1100 }}
          size="small"
        />
      </Card>
      
      {/* 優化的詳情 Modal */}
      <Modal
        title="投注詳情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={null}
        width={700}
      >
        {selectedRecord && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="投注編號" span={2}>
              <Text code>{selectedRecord.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="回合編號" span={2}>
              <Text code>{selectedRecord.roundId}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="投注時間" span={2}>
              {formatUtils.formatDateTime(selectedRecord.createdAt)}
            </Descriptions.Item>
            
            <Descriptions.Item label="會員帳號">
              {selectedRecord.userId}
            </Descriptions.Item>
            <Descriptions.Item label="會員名稱">
              {selectedRecord.user?.displayName || '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label="投注代理" span={2}>
              {selectedRecord.agent?.displayName || 
               selectedRecord.agent?.username || 
               (selectedRecord.agentId ? `代理ID: ${selectedRecord.agentId.slice(-6)}` : '-')}
            </Descriptions.Item>
            
            <Descriptions.Item label="投注類型">
              <Space direction="vertical" size={4}>
                <Tag color="blue" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                  {formatUtils.getBetTypeName(selectedRecord.betType)}
                </Tag>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {formatUtils.getBetTypeDescription(selectedRecord.betType)}
                </Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="投注內容">
              {(() => {
                const betContent = formatUtils.getBetContentDisplay(selectedRecord.betType, selectedRecord.betContent, selectedRecord);
                return (
                  <Space direction="vertical" size={2}>
                    <Text strong>{betContent.main}</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {betContent.detail}
                    </Text>
                  </Space>
                );
              })()}
            </Descriptions.Item>
            
            <Descriptions.Item label="投注金額">
              <Text strong style={{ color: '#1890ff' }}>
                ${selectedRecord.betAmount?.toLocaleString()}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="中獎金額">
              <Text strong style={{ color: '#52c41a' }}>
                ${selectedRecord.winAmount?.toLocaleString()}
              </Text>
            </Descriptions.Item>
            
            <Descriptions.Item label="會員盈虧" span={2}>
              {(() => {
                const winLoss = formatUtils.formatWinLoss(selectedRecord.betAmount, selectedRecord.winAmount);
                return (
                  <Text strong style={{ 
                    color: winLoss.memberWinLoss > 0 ? '#52c41a' : '#ff4d4f' 
                  }}>
                    {winLoss.memberText}
                  </Text>
                );
              })()}
            </Descriptions.Item>
            
            <Descriptions.Item label="狀態" span={2}>
              {(() => {
                const statusInfo = formatUtils.getStatusDisplay(selectedRecord.status);
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
              })()}
            </Descriptions.Item>
            
            {selectedRecord.status !== 'pending' && selectedRecord.round?.result && (
              <Descriptions.Item label="開獎結果" span={2}>
                <Text code>
                  {formatUtils.formatGameResult(selectedRecord.round.result)}
                </Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default BettingRecords;
